<template>
  <div>
    <header-component />
    <div class="container-fluid">
      <div class="overflow-hidden bg-white rounded-xl border border-slate-200 shadow-sm">
        <div
          class="flex flex-wrap gap-3 justify-between items-center px-4 py-4 border-b border-slate-200 sm:px-6"
        >
          <div>
            <h3 class="text-lg font-semibold tracking-tight text-slate-900">
              Vendor reports
            </h3>
            <p class="mt-0.5 text-sm text-slate-500">
              {{ vendorDisplayName }}
            </p>
          </div>
          <base-button size="sm" type="secondary" @click="goToVendorList">
            ← Vendors list
          </base-button>
        </div>

        <div class="px-4 py-6 sm:px-6">
          <!-- Drill breadcrumb -->
          <div v-if="drillStep > 0" class="flex flex-wrap gap-1 items-center mb-4">
            <el-button type="text" size="small" class="!p-0" @click="drillBackAll">
              Summary
            </el-button>
            <span v-if="reportLabel" class="text-slate-400">/</span>
            <el-button
              v-if="reportLabel && drillStep > 1"
              type="text"
              size="small"
              class="!p-0"
              @click="drillBackToMonths"
            >
              {{ reportLabel }}
            </el-button>
            <span v-if="selectedMonthTitle && drillStep > 2" class="text-slate-400"
              >/</span
            >
            <el-button
              v-if="selectedMonthTitle && drillStep > 2"
              type="text"
              size="small"
              class="!p-0"
              @click="drillBackToDays"
            >
              {{ selectedMonthTitle }}
            </el-button>
            <span v-if="selectedDayLabel && drillStep === 3" class="text-slate-400"
              >/</span
            >
            <span
              v-if="selectedDayLabel && drillStep === 3"
              class="text-sm font-medium text-slate-700"
            >
              {{ selectedDayLabel }}
            </span>
          </div>

          <!-- Summary -->
          <div v-if="drillStep === 0" v-loading="loadingCounts">
            <div
              class="p-1 mb-6 rounded-xl bg-slate-200/90 ring-1 ring-slate-300/60"
            >
              <button
                type="button"
                class="flex justify-between items-center px-4 py-3 w-full text-left rounded-t-lg transition hover:bg-white/60"
                @click="startReport('revenue')"
              >
                <span class="text-sm font-medium text-slate-800"
                  >Monthly billed work (invoice)</span
                >
                <span class="text-sm font-bold tabular-nums text-slate-900"
                  >₹ {{ formatMoney(counts.serviceRequestLaborTotal) }}</span
                >
              </button>
              <div class="mx-3 h-px bg-slate-300/80" />
              <button
                type="button"
                class="flex justify-between items-center px-4 py-3 w-full text-left transition hover:bg-white/60"
                @click="startReport('transactions')"
              >
                <span class="text-sm font-medium text-slate-800"
                  >Monthly received (completed)</span
                >
                <span class="text-sm font-bold tabular-nums text-slate-900"
                  >₹
                  {{
                    formatMoney(counts.monthlyLaborTotalForClosedServiceRequests)
                  }}</span
                >
              </button>
              <div class="mx-3 h-px bg-slate-300/80" />
              <button
                type="button"
                class="flex justify-between items-center px-4 py-3 w-full text-left rounded-b-lg transition hover:bg-white/60"
                @click="startReport('outstanding')"
              >
                <span class="text-sm font-medium text-slate-800"
                  >Outstanding</span
                >
                <span class="text-sm font-bold tabular-nums text-slate-900"
                  >₹ {{ formatMoney(counts.outstanding) }}</span
                >
              </button>
            </div>

            <div class="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-3">
              <div class="px-4 py-3 rounded-lg border bg-slate-50 border-slate-200">
                <div class="text-xs font-medium tracking-wide uppercase text-slate-500">
                  Ongoing requests
                </div>
                <div class="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
                  {{ counts.ongoingServiceRequests || 0 }}
                </div>
              </div>
              <div class="px-4 py-3 rounded-lg border bg-slate-50 border-slate-200">
                <div class="text-xs font-medium tracking-wide uppercase text-slate-500">
                  Points (month)
                </div>
                <div
                  class="mt-1 text-2xl font-semibold tabular-nums text-emerald-600"
                >
                  {{ counts.vendorPoints || 0 }}
                </div>
              </div>
              <div class="px-4 py-3 rounded-lg border bg-slate-50 border-slate-200">
                <div class="text-xs font-medium tracking-wide uppercase text-slate-500">
                  Penalty (month)
                </div>
                <div class="mt-1 text-2xl font-semibold tabular-nums text-red-600">
                  {{ counts.vendorPenalty || 0 }}
                </div>
              </div>
            </div>

            <p class="text-xs leading-relaxed text-slate-500">
              Tap a summary row to view month-wise figures, then a month for
              day-wise totals, then a day for request-level detail (same flow as
              the vendor mobile app — Rajput = chalans, Rajdoot = labor + external
              goods).
            </p>
          </div>

          <!-- Tables -->
          <div v-else v-loading="loadingDetail">
            <div class="flex justify-between items-center mb-4">
              <h4 class="text-base font-semibold text-slate-900">
                {{ reportTitle }}
              </h4>
              <el-button size="small" @click="drillBack">← Back</el-button>
            </div>

            <div class="overflow-x-auto rounded-xl border border-slate-200">
              <!-- Month / Day aggregate tables -->
              <table
                v-if="drillStep === 1 || drillStep === 2"
                class="min-w-full text-sm divide-y divide-slate-200"
              >
                <thead class="bg-slate-100 text-slate-700">
                  <tr>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-left uppercase">
                      {{ drillStep === 1 ? "Period" : "Day" }}
                    </th>
                    <th
                      v-if="reportKind === 'outstanding'"
                      class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase"
                    >
                      Rajput
                    </th>
                    <th
                      v-if="reportKind === 'outstanding'"
                      class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase"
                    >
                      Rajdoot
                    </th>
                    <th
                      v-if="reportKind === 'revenue'"
                      class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase"
                    >
                      Labor
                    </th>
                    <th
                      v-if="reportKind === 'revenue'"
                      class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase"
                    >
                      External
                    </th>
                    <th
                      v-if="reportKind === 'revenue'"
                      class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase"
                    >
                      Chalans
                    </th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">
                      {{
                        reportKind === "outstanding"
                          ? "Total"
                          : reportKind === "transactions"
                          ? "Amount"
                          : "Total"
                      }}
                    </th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">
                      Requests
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-slate-200">
                  <tr
                    v-for="row in aggregateRows"
                    :key="`agg-${row.title}`"
                    class="cursor-pointer hover:bg-blue-50"
                    @click="onAggregateRowClick(row)"
                  >
                    <td class="px-4 py-3 font-medium">
                      {{ row.title }}
                      <span
                        v-if="reportKind === 'outstanding'"
                        class="ml-1 text-xs font-normal text-slate-500"
                      >
                        ({{ row.count || 0 }})
                      </span>
                    </td>
                    <td
                      v-if="reportKind === 'outstanding'"
                      class="px-4 py-3 text-right"
                    >
                      ₹ {{ formatMoney(rajputAmount(row)) }}
                    </td>
                    <td
                      v-if="reportKind === 'outstanding'"
                      class="px-4 py-3 text-right"
                    >
                      ₹ {{ formatMoney(rajdootAmount(row)) }}
                    </td>
                    <td
                      v-if="reportKind === 'revenue'"
                      class="px-4 py-3 text-right"
                    >
                      ₹ {{ formatMoney(laborAmount(row)) }}
                    </td>
                    <td
                      v-if="reportKind === 'revenue'"
                      class="px-4 py-3 text-right"
                    >
                      ₹ {{ formatMoney(externalGoodsAmount(row)) }}
                    </td>
                    <td
                      v-if="reportKind === 'revenue'"
                      class="px-4 py-3 text-right"
                    >
                      ₹ {{ formatMoney(chalansAmount(row)) }}
                    </td>
                    <td class="px-4 py-3 text-right">
                      ₹ {{ formatMoney(aggregateDisplayTotal(row)) }}
                    </td>
                    <td class="px-4 py-3 text-right">{{ row.count || 0 }}</td>
                  </tr>
                  <tr v-if="aggregateRows.length" class="font-semibold bg-slate-50">
                    <td class="px-4 py-3">Total</td>
                    <td
                      v-if="reportKind === 'outstanding'"
                      class="px-4 py-3 text-right"
                    >
                      ₹ {{ formatMoney(sumMapped(aggregateRows, rajputAmount)) }}
                    </td>
                    <td
                      v-if="reportKind === 'outstanding'"
                      class="px-4 py-3 text-right"
                    >
                      ₹ {{ formatMoney(sumMapped(aggregateRows, rajdootAmount)) }}
                    </td>
                    <td
                      v-if="reportKind === 'revenue'"
                      class="px-4 py-3 text-right"
                    >
                      ₹ {{ formatMoney(sumMapped(aggregateRows, laborAmount)) }}
                    </td>
                    <td
                      v-if="reportKind === 'revenue'"
                      class="px-4 py-3 text-right"
                    >
                      ₹
                      {{
                        formatMoney(sumMapped(aggregateRows, externalGoodsAmount))
                      }}
                    </td>
                    <td
                      v-if="reportKind === 'revenue'"
                      class="px-4 py-3 text-right"
                    >
                      ₹ {{ formatMoney(sumMapped(aggregateRows, chalansAmount)) }}
                    </td>
                    <td class="px-4 py-3 text-right">
                      ₹
                      {{
                        formatMoney(
                          sumMapped(aggregateRows, aggregateDisplayTotal)
                        )
                      }}
                    </td>
                    <td class="px-4 py-3 text-right">
                      {{ sumBy(aggregateRows, "count") }}
                    </td>
                  </tr>
                  <tr v-if="!aggregateRows.length">
                    <td
                      :colspan="aggregateColSpan"
                      class="px-4 py-6 text-sm text-center text-slate-500"
                    >
                      No data found.
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Request-level (v2) -->
              <table v-else class="min-w-full text-sm divide-y divide-slate-200">
                <thead class="bg-slate-100 text-slate-700">
                  <tr>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-left uppercase">
                      SR No.
                    </th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-left uppercase">
                      Customer
                    </th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-left uppercase">
                      Phone
                    </th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-left uppercase">
                      Branch
                    </th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">
                      Rajput
                    </th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">
                      Rajdoot
                    </th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">
                      Paid
                    </th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">
                      Outstanding
                    </th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-slate-200">
                  <template
                    v-for="row in requestRows"
                  >
                    <tr
                      :key="`req-${requestRowKey(row)}`"
                      class="cursor-pointer hover:bg-slate-50"
                      @click="toggleRequestExpand(row)"
                    >
                      <td class="px-4 py-3">
                        <router-link
                          v-if="row.serviceRequestId"
                          :to="'/serviceRequests/flow/' + row.serviceRequestId"
                          class="font-medium text-indigo-600 hover:text-indigo-800"
                          @click.native.stop
                        >
                          {{ getServiceRequestNumber(row) }}
                        </router-link>
                        <span v-else>{{ getServiceRequestNumber(row) }}</span>
                      </td>
                      <td class="px-4 py-3">{{ getCustomerDisplay(row) }}</td>
                      <td class="px-4 py-3 whitespace-nowrap">
                        {{ getPhoneDisplay(row) }}
                      </td>
                      <td class="px-4 py-3">{{ row.branchName || "—" }}</td>
                      <td class="px-4 py-3 text-right">
                        ₹ {{ formatMoney(requestRajputAmount(row)) }}
                      </td>
                      <td class="px-4 py-3 text-right">
                        ₹ {{ formatMoney(requestRajdootAmount(row)) }}
                      </td>
                      <td class="px-4 py-3 text-right">
                        ₹ {{ formatMoney(paidAmount(row)) }}
                      </td>
                      <td class="px-4 py-3 text-right">
                        ₹ {{ formatMoney(pendingAmount(row)) }}
                      </td>
                      <td class="px-4 py-3 text-right font-medium">
                        ₹ {{ formatMoney(requestDisplayTotal(row)) }}
                      </td>
                    </tr>
                    <tr
                      v-if="isRequestExpanded(row)"
                      :key="`exp-${row.serviceRequestId || row.serviceRequestNumber}`"
                      class="bg-slate-50"
                    >
                      <td colspan="9" class="px-4 py-3">
                        <div class="grid gap-3 sm:grid-cols-2">
                          <div class="p-3 rounded-lg border border-slate-200 bg-white">
                            <div class="mb-2 text-xs font-semibold tracking-wide uppercase text-slate-500">
                              Rajput bill (chalans)
                            </div>
                            <div class="flex justify-between text-sm">
                              <span>Bill</span>
                              <span>₹ {{ formatMoney(chalansAmount(row)) }}</span>
                            </div>
                            <div class="flex justify-between text-sm">
                              <span>Received</span>
                              <span>₹ {{ formatMoney(row.rajputPaid) }}</span>
                            </div>
                            <div class="flex justify-between text-sm font-medium">
                              <span>Balance</span>
                              <span
                                >₹ {{ formatMoney(row.rajputOutstanding) }}</span
                              >
                            </div>
                          </div>
                          <div class="p-3 rounded-lg border border-slate-200 bg-white">
                            <div class="mb-2 text-xs font-semibold tracking-wide uppercase text-slate-500">
                              Rajdoot bill (labor + external)
                            </div>
                            <div class="flex justify-between text-sm">
                              <span>Labor</span>
                              <span>₹ {{ formatMoney(laborAmount(row)) }}</span>
                            </div>
                            <div class="flex justify-between text-sm">
                              <span>External</span>
                              <span
                                >₹
                                {{ formatMoney(externalGoodsAmount(row)) }}</span
                              >
                            </div>
                            <div class="flex justify-between text-sm">
                              <span>Bill</span>
                              <span
                                >₹ {{ formatMoney(requestRajdootAmount(row)) }}</span
                              >
                            </div>
                            <div class="flex justify-between text-sm">
                              <span>Received</span>
                              <span>₹ {{ formatMoney(row.rajdootPaid) }}</span>
                            </div>
                            <div class="flex justify-between text-sm font-medium">
                              <span>Balance</span>
                              <span
                                >₹ {{ formatMoney(row.rajdootOutstanding) }}</span
                              >
                            </div>
                          </div>
                        </div>
                        <div
                          v-if="row.assistants && row.assistants.length"
                          class="mt-3 text-xs text-slate-600"
                        >
                          Assistants:
                          {{
                            row.assistants
                              .map(
                                a =>
                                  `${a.name || "—"} (₹ ${formatMoney(a.labor)})`
                              )
                              .join(", ")
                          }}
                        </div>
                      </td>
                    </tr>
                  </template>
                  <tr v-if="requestRows.length" class="font-semibold bg-slate-50">
                    <td class="px-4 py-3" colspan="4">Total</td>
                    <td class="px-4 py-3 text-right">
                      ₹
                      {{
                        formatMoney(
                          sumMapped(requestRows, requestRajputAmount)
                        )
                      }}
                    </td>
                    <td class="px-4 py-3 text-right">
                      ₹
                      {{
                        formatMoney(
                          sumMapped(requestRows, requestRajdootAmount)
                        )
                      }}
                    </td>
                    <td class="px-4 py-3 text-right">
                      ₹ {{ formatMoney(sumMapped(requestRows, paidAmount)) }}
                    </td>
                    <td class="px-4 py-3 text-right">
                      ₹ {{ formatMoney(sumMapped(requestRows, pendingAmount)) }}
                    </td>
                    <td class="px-4 py-3 text-right">
                      ₹
                      {{
                        formatMoney(
                          sumMapped(requestRows, requestDisplayTotal)
                        )
                      }}
                    </td>
                  </tr>
                  <tr v-if="requestRows.length === 0">
                    <td
                      colspan="9"
                      class="px-4 py-6 text-sm text-center text-slate-500"
                    >
                      No request details found for this day.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Button } from "element-ui";
import HeaderComponent from "../Components/HeaderComponent";
import { apiHelper } from "@/utils/helpers";
import endpoints from "@/utils/constants";
import snakeToCamel from "@/utils/SnakeToCamel";

export default {
  name: "VendorReports",
  components: {
    [Button.name]: Button,
    "header-component": HeaderComponent
  },
  data() {
    return {
      counts: {},
      loadingCounts: false,
      loadingDetail: false,
      drillStep: 0,
      reportKind: null,
      monthRows: [],
      dayRows: [],
      requestRows: [],
      selectedMonthTitle: null,
      selectedDayLabel: null,
      expandedRequestIds: {}
    };
  },
  computed: {
    vendorId() {
      return this.$route.params.vendorId;
    },
    vendorDisplayName() {
      const q = this.$route.query.name;
      return q ? String(q) : "Vendor";
    },
    reportLabel() {
      if (this.reportKind === "revenue") return "Monthly revenue";
      if (this.reportKind === "transactions") return "Monthly transactions";
      if (this.reportKind === "outstanding") return "Monthly outstanding";
      return "";
    },
    reportTitle() {
      if (this.drillStep === 1) return this.reportLabel;
      if (this.drillStep === 2)
        return `${this.reportLabel} — ${this.selectedMonthTitle || ""}`;
      return `Request detail — ${this.selectedDayLabel || ""}`;
    },
    aggregateRows() {
      return this.drillStep === 1 ? this.monthRows : this.dayRows;
    },
    aggregateColSpan() {
      if (this.reportKind === "revenue") return 6;
      if (this.reportKind === "outstanding") return 5;
      return 3;
    }
  },
  watch: {
    vendorId: {
      immediate: true,
      handler() {
        this.resetDrill();
        this.loadCounts();
      }
    }
  },
  methods: {
    baseUrl() {
      return `${endpoints.LARAVEL_BASE_URL}/api/v1`;
    },
    baseUrlV2() {
      return `${endpoints.LARAVEL_BASE_URL}/api/v2`;
    },
    todayParam() {
      const d = new Date();
      const p = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    },
    num(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    },
    formatMoney(v) {
      const n = this.num(v);
      return n.toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    },
    normalizeRows(data) {
      const raw = Array.isArray(data)
        ? data
        : data && Array.isArray(data.data)
        ? data.data
        : data && Array.isArray(data.requests)
        ? data.requests
        : data && Array.isArray(data.items)
        ? data.items
        : [];
      return snakeToCamel(raw);
    },

    // --- Field helpers (aligned with mobile ApiAggregateFields) ---
    laborAmount(row) {
      return this.num(row.labor != null ? row.labor : row.rajdootBill);
    },
    chalansAmount(row) {
      return this.num(row.chalans != null ? row.chalans : row.rajputBill);
    },
    externalGoodsAmount(row) {
      return this.num(row.externalGoods);
    },
    paidAmount(row) {
      return this.num(
        row.amountPaid != null
          ? row.amountPaid
          : row.paid != null
          ? row.paid
          : row.transactions
      );
    },
    pendingAmount(row) {
      const direct = row.outstanding != null ? row.outstanding : row.pending;
      if (direct != null) return this.num(direct);
      const total = this.displayTotalFrom(row);
      const paid = this.paidAmount(row);
      return Math.max(0, total - paid);
    },
    displayTotalFrom(row) {
      const total = this.num(row.total != null ? row.total : row.totalSum);
      if (total > 0) return total;
      return (
        this.laborAmount(row) +
        this.chalansAmount(row) +
        this.externalGoodsAmount(row)
      );
    },
    rajputAmount(row) {
      return this.chalansAmount(row);
    },
    rajdootAmount(row) {
      return this.laborAmount(row) + this.externalGoodsAmount(row);
    },
    /** Month/day aggregate primary amount — matches mobile displayTotal / pending. */
    aggregateDisplayTotal(row) {
      if (this.reportKind === "outstanding") {
        const pending = this.pendingAmount(row);
        return pending > 0 ? pending : this.displayTotalFrom(row);
      }
      if (this.reportKind === "transactions") {
        // Mobile received month/day shows labor as the primary amount.
        const labor = this.laborAmount(row);
        return labor > 0 ? labor : this.displayTotalFrom(row);
      }
      return this.displayTotalFrom(row);
    },
    requestRajputAmount(row) {
      if (this.reportKind === "outstanding") {
        const pending = this.num(row.rajputOutstanding);
        if (pending > 0) return pending;
      }
      return this.chalansAmount(row);
    },
    requestRajdootAmount(row) {
      if (this.reportKind === "outstanding") {
        const pending = this.num(row.rajdootOutstanding);
        if (pending > 0) return pending;
      }
      return this.rajdootAmount(row);
    },
    requestDisplayTotal(row) {
      if (this.reportKind === "outstanding") {
        const pending = this.pendingAmount(row);
        return pending > 0 ? pending : this.displayTotalFrom(row);
      }
      return this.displayTotalFrom(row);
    },

    sumBy(rows, key) {
      return (rows || []).reduce((acc, r) => acc + this.num(r[key]), 0);
    },
    sumMapped(rows, mapper) {
      return (rows || []).reduce((acc, r) => acc + this.num(mapper(r)), 0);
    },

    getServiceRequestNumber(row) {
      return (
        row.serviceRequestNumber ||
        row.serviceRequestNo ||
        row.identifier ||
        "-"
      );
    },
    getCustomerDisplay(row) {
      const candidate =
        row.customer ||
        row.customerName ||
        row.customer_name ||
        row.userName ||
        row.name ||
        "";
      const text = String(candidate).trim();
      const normalized = text.replace(/\s+/g, " ").toLowerCase();
      if (
        !text ||
        normalized === "0" ||
        normalized === "₹ 0" ||
        normalized === "rs 0" ||
        normalized === "null" ||
        normalized === "undefined" ||
        normalized === "n/a"
      ) {
        return "-";
      }
      return text;
    },
    getPhoneDisplay(row) {
      const val = row.phone || row.customerPhone || row.mobile || "";
      const text = String(val).replace(/\s+/g, "").trim();
      return text || "-";
    },

    requestRowKey(row) {
      return row.serviceRequestId || row.serviceRequestNumber || "";
    },
    isRequestExpanded(row) {
      return !!this.expandedRequestIds[this.requestRowKey(row)];
    },
    toggleRequestExpand(row) {
      const key = this.requestRowKey(row);
      if (!key) return;
      this.$set(this.expandedRequestIds, key, !this.expandedRequestIds[key]);
    },

    goToVendorList() {
      this.$router.push("/vendors/list");
    },
    resetDrill() {
      this.drillStep = 0;
      this.reportKind = null;
      this.monthRows = [];
      this.dayRows = [];
      this.requestRows = [];
      this.selectedMonthTitle = null;
      this.selectedDayLabel = null;
      this.expandedRequestIds = {};
    },
    async loadCounts() {
      if (!this.vendorId) return;
      this.loadingCounts = true;
      try {
        const res = await apiHelper.call(
          "get",
          `${this.baseUrl()}/vendors/${this.vendorId}/counts?date=${this.todayParam()}`
        );
        const body = res.data;
        this.counts =
          body && typeof body === "object" && !Array.isArray(body)
            ? snakeToCamel(body)
            : {};
      } catch (e) {
        this.$notify({
          type: "danger",
          title: "Error",
          message: "Could not load vendor summary."
        });
        this.counts = {};
      } finally {
        this.loadingCounts = false;
      }
    },
    parseMonthYear(title) {
      if (!title) return null;
      const parts = String(title).trim().split(/\s+/);
      if (parts.length < 2) return null;
      return { month: parts[0], year: parts[1] };
    },
    startReport(kind) {
      this.reportKind = kind;
      this.drillStep = 1;
      this.selectedMonthTitle = null;
      this.selectedDayLabel = null;
      this.dayRows = [];
      this.requestRows = [];
      this.expandedRequestIds = {};
      this.loadMonthwise();
    },
    async loadMonthwise() {
      this.loadingDetail = true;
      try {
        let url;
        if (this.reportKind === "revenue") {
          url = `${this.baseUrl()}/vendors/${this.vendorId}/revenue/monthwise`;
        } else if (this.reportKind === "transactions") {
          url = `${this.baseUrl()}/vendors/${
            this.vendorId
          }/transactions/monthwise`;
        } else {
          url = `${this.baseUrl()}/vendors/${
            this.vendorId
          }/outstanding/monthwise`;
        }
        const res = await apiHelper.call("get", url);
        this.monthRows = this.normalizeRows(res.data);
      } catch (e) {
        this.$notify({
          type: "danger",
          title: "Error",
          message: "Could not load month-wise data."
        });
        this.monthRows = [];
      } finally {
        this.loadingDetail = false;
      }
    },
    onAggregateRowClick(row) {
      if (this.drillStep === 1) {
        this.onMonthRowClick(row);
      } else {
        this.onDayRowClick(row);
      }
    },
    onMonthRowClick(row) {
      const my = this.parseMonthYear(row.title);
      if (!my) {
        this.$notify({
          type: "warning",
          title: "Parse error",
          message: "Could not read month from row."
        });
        return;
      }
      this.selectedMonthTitle = row.title;
      this.drillStep = 2;
      this.loadDaywise(my.month, my.year);
    },
    async loadDaywise(month, year) {
      this.loadingDetail = true;
      this.dayRows = [];
      try {
        let url;
        if (this.reportKind === "revenue") {
          url = `${this.baseUrl()}/vendors/${
            this.vendorId
          }/revenue/daywise/${encodeURIComponent(month)}/${encodeURIComponent(
            year
          )}`;
        } else if (this.reportKind === "transactions") {
          url = `${this.baseUrl()}/vendors/${
            this.vendorId
          }/transactions/daywise/${encodeURIComponent(
            month
          )}/${encodeURIComponent(year)}`;
        } else {
          url = `${this.baseUrl()}/vendors/${
            this.vendorId
          }/outstanding/daywise/${encodeURIComponent(
            month
          )}/${encodeURIComponent(year)}`;
        }
        const res = await apiHelper.call("get", url);
        this.dayRows = this.normalizeRows(res.data);
      } catch (e) {
        this.$notify({
          type: "danger",
          title: "Error",
          message: "Could not load day-wise data."
        });
      } finally {
        this.loadingDetail = false;
      }
    },
    onDayRowClick(row) {
      const date = row.title;
      if (!date) return;
      this.selectedDayLabel = date;
      this.drillStep = 3;
      this.expandedRequestIds = {};
      this.loadDayRequests(date);
    },
    async loadDayRequests(date) {
      this.loadingDetail = true;
      this.requestRows = [];
      try {
        // Match mobile: day → request lists use Laravel v2 day APIs.
        const enc = encodeURIComponent(date);
        let url;
        if (this.reportKind === "revenue") {
          url = `${this.baseUrlV2()}/vendors/${
            this.vendorId
          }/revenue/day/${enc}`;
        } else if (this.reportKind === "transactions") {
          url = `${this.baseUrlV2()}/vendors/${
            this.vendorId
          }/transactions/day/${enc}`;
        } else {
          url = `${this.baseUrlV2()}/vendors/${
            this.vendorId
          }/outstanding/day/${enc}`;
        }
        const res = await apiHelper.call("get", url);
        this.requestRows = this.normalizeRows(res.data);
      } catch (e) {
        this.$notify({
          type: "danger",
          title: "Error",
          message: "Could not load requests for this day."
        });
      } finally {
        this.loadingDetail = false;
      }
    },
    drillBack() {
      if (this.drillStep === 3) {
        this.drillStep = 2;
        this.selectedDayLabel = null;
        this.requestRows = [];
        this.expandedRequestIds = {};
        return;
      }
      if (this.drillStep === 2) {
        this.drillStep = 1;
        this.selectedMonthTitle = null;
        this.dayRows = [];
        return;
      }
      if (this.drillStep === 1) {
        this.resetDrill();
      }
    },
    drillBackToMonths() {
      this.drillStep = 1;
      this.selectedMonthTitle = null;
      this.selectedDayLabel = null;
      this.dayRows = [];
      this.requestRows = [];
      this.expandedRequestIds = {};
    },
    drillBackToDays() {
      this.drillStep = 2;
      this.selectedDayLabel = null;
      this.requestRows = [];
      this.expandedRequestIds = {};
    },
    drillBackAll() {
      this.resetDrill();
    }
  }
};
</script>

<style scoped></style>
